'use client';

import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import posthog from 'posthog-js';
import { PostHogProvider as PostHogReactProvider } from 'posthog-js/react';

import { getLogger } from '@kit/shared/logger';

import posthogConfig from '..//config/posthog.config';

// Initialize PostHog only on client side
if (typeof window !== 'undefined' && posthogConfig.enabled) {
  try {
    posthog.init(posthogConfig.key, {
      api_host: posthogConfig.host,
      loaded: () => {
        if (process.env.NODE_ENV === 'development') {
          // Add warning in development
          console.log('[PostHog] Initialized in development mode');
        }
      },
      capture_pageview: false, // We'll handle this manually
      persistence: 'localStorage',
      autocapture: true,
      person_profiles: 'identified_only', // Only create profiles for identified users
    });
  } catch (error) {
    console.error('[PostHog] Failed to initialize:', error);
  }
}

interface PostHogProviderProps {
  children: React.ReactNode;
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track page views
  useEffect(() => {
    if (!posthogConfig.enabled) {
      return;
    }

    const capturePageView = () => {
      try {
        const currentUrl =
          pathname +
          (searchParams?.toString() ? `?${searchParams.toString()}` : '');

        posthog.capture('$pageview', {
          $current_url: currentUrl,
        });
      } catch (error) {
        const logger = getLogger();
        logger.error(
          { error, pathname, searchParams: searchParams?.toString() },
          'Failed to capture page view',
        );
      }
    };

    void capturePageView();
  }, [pathname, searchParams]);

  if (!posthogConfig.enabled) {
    return <>{children}</>;
  }

  return (
    <PostHogReactProvider client={posthog}>{children}</PostHogReactProvider>
  );
}
